/**
 * Web API 服务层
 * 提供统一的 API 接口，通过 HTTP 请求后端
 */

// 使用相对路径，让 Vite 代理处理
const API_BASE = '/api';

// HTTP 请求封装
async function request(endpoint, options = {}) {
  const url = `${API_BASE}${endpoint}`;
  const headers = {
    'Content-Type': 'application/json',
    ...options.headers
  };
  const token = typeof window !== 'undefined' && window.localStorage ? window.localStorage.getItem('token') : null;
  if (token) headers['Authorization'] = `Bearer ${token}`;
  const config = {
    headers,
    credentials: 'include',
    ...options
  };

  if (options.body && typeof options.body === 'object') {
    config.body = JSON.stringify(options.body);
  }

  const response = await fetch(url, config);
  
  if (!response.ok) {
    const error = await response.json().catch(() => ({ error: response.statusText }));
    throw new Error(error.error || '请求失败');
  }

  return response.json();
}

// Web API 实现
const webApiImpl = {
  // ========== 配置管理 ==========
  async getConfig() {
    return request('/config');
  },

  async saveConfig(config) {
    return request('/config', { method: 'POST', body: config });
  },

  // ========== 分组管理 ==========
  async getGroups() {
    return request('/config/groups');
  },

  async saveGroups(groups) {
    return request('/config/groups', { method: 'POST', body: groups });
  },

  // ========== 应用项管理 ==========
  async getItems() {
    return request('/config/items');
  },

  async saveItems(items) {
    return request('/config/items', { method: 'POST', body: items });
  },

  // ========== 文件备注 ==========
  async getFileNote(filePath) {
    const result = await request(`/config/file-note?filePath=${encodeURIComponent(filePath)}`);
    return result.note;
  },

  async saveFileNote(filePath, note) {
    return request('/config/file-note', { method: 'POST', body: { filePath, note } });
  },

  // ========== 代理请求 ==========
  async proxyFetch(options) {
    return request('/proxy', { method: 'POST', body: options });
  },

  // ========== AI API 代理 ==========
  async aiRequest(options) {
    return request('/proxy/ai', { method: 'POST', body: options });
  },

  // ========== 期货数据 ==========
  async fetchFuturesData(type, params = {}) {
    const queryString = new URLSearchParams(params).toString();
    return request(`/proxy/futures/${type}?${queryString}`);
  },

  // ========== 服务管理 ==========
  async getAllServicesStatus() {
    return request('/services/status');
  },

  async startService(port) {
    return request(`/services/start/${port}`, { method: 'POST' });
  },

  async stopService(port) {
    return request(`/services/stop/${port}`, { method: 'POST' });
  },

  async startAllServices() {
    return request('/services/start-all', { method: 'POST' });
  },

  async stopAllServices() {
    return request('/services/stop-all', { method: 'POST' });
  },

  // ========== Web 环境下不可用的功能（返回空或模拟数据）==========
  
  // 桌面扫描（Web 环境不可用）
  async scanDesktop() {
    console.warn('scanDesktop: Web 环境下不可用');
    return [];
  },

  // 打开文件（Web 环境下打开链接）
  async launchFile(fullPath) {
    if (fullPath.startsWith('http://') || fullPath.startsWith('https://')) {
      window.open(fullPath, '_blank');
      return { success: true };
    }
    console.warn('launchFile: Web 环境下只能打开网页链接');
    return { success: false, error: 'Web 环境下只能打开网页链接' };
  },

  // 文件选择（使用浏览器原生文件选择）
  async selectFile() {
    return new Promise((resolve) => {
      const input = document.createElement('input');
      input.type = 'file';
      input.onchange = (e) => {
        const file = e.target.files[0];
        if (file) {
          resolve({ name: file.name, path: file.name, file });
        } else {
          resolve(null);
        }
      };
      input.click();
    });
  },

  // 图片选择
  async selectImage() {
    return new Promise((resolve) => {
      const input = document.createElement('input');
      input.type = 'file';
      input.accept = 'image/*';
      input.onchange = async (e) => {
        const file = e.target.files[0];
        if (file) {
          const reader = new FileReader();
          reader.onload = () => resolve(reader.result);
          reader.readAsDataURL(file);
        } else {
          resolve(null);
        }
      };
      input.click();
    });
  },

  // 数据文件选择
  async selectDataFile() {
    return new Promise((resolve) => {
      const input = document.createElement('input');
      input.type = 'file';
      input.accept = '.xlsx,.xls,.csv,.json';
      input.onchange = (e) => {
        const file = e.target.files[0];
        if (file) {
          resolve({ name: file.name, path: file.name, file });
        } else {
          resolve(null);
        }
      };
      input.click();
    });
  },

  // 导入数据文件
  async importDataFile(fileOrPath) {
    if (fileOrPath instanceof File || (fileOrPath && fileOrPath.file)) {
      const file = fileOrPath instanceof File ? fileOrPath : fileOrPath.file;
      return new Promise((resolve, reject) => {
        const reader = new FileReader();
        reader.onload = (e) => {
          try {
            const ext = file.name.split('.').pop().toLowerCase();
            if (ext === 'json') {
              resolve(JSON.parse(e.target.result));
            } else if (ext === 'csv') {
              // 简单 CSV 解析
              const lines = e.target.result.split('\n');
              const headers = lines[0].split(',').map(h => h.trim());
              const data = lines.slice(1).filter(l => l.trim()).map(line => {
                const values = line.split(',');
                const obj = {};
                headers.forEach((h, i) => obj[h] = values[i]?.trim());
                return obj;
              });
              resolve(data);
            } else {
              // Excel 文件需要使用 xlsx 库
              resolve({ raw: e.target.result, type: ext });
            }
          } catch (err) {
            reject(err);
          }
        };
        reader.onerror = reject;
        if (file.name.endsWith('.xlsx') || file.name.endsWith('.xls')) {
          reader.readAsArrayBuffer(file);
        } else {
          reader.readAsText(file);
        }
      });
    }
    console.warn('importDataFile: 需要传入 File 对象');
    return null;
  },

  // 窗口操作（Web 环境下无效）
  showWindow: () => Promise.resolve(),
  minimizeWindow: () => Promise.resolve(),
  maximizeWindow: () => Promise.resolve(),
  closeWindow: () => window.close(),
  restoreWindow: () => Promise.resolve(),

  // 获取桌面路径（Web 环境返回空）
  getDesktopPath: () => Promise.resolve(''),

  // 图标相关（Web 环境返回空）
  getShortcutIcon: () => Promise.resolve(null),
  getFileIcon: () => Promise.resolve(null),
  getFileIconsBatch: () => Promise.resolve([]),

  // 主题更新
  updateWindowTheme: () => Promise.resolve(),

  // 检查服务状态
  async checkOCRService() {
    try {
      const response = await fetch('http://localhost:8866/health');
      return response.ok;
    } catch {
      return false;
    }
  },

  async checkDeepseekOCRService() {
    try {
      const response = await fetch('http://localhost:8868/health');
      return response.ok;
    } catch {
      return false;
    }
  },

  // 事件监听（Web 环境使用空函数）
  onSwitchToGroup: () => {},
  removeSwitchToGroupListener: () => {},
  onFactorAnalysisData: () => {},
  removeFactorAnalysisDataListener: () => {},
  onBVPrimaryChanged: () => {},
  removeBVPrimaryChangedListener: () => {},
  onSyncClientConnect: () => {},
  onSyncClientDisconnect: () => {},
  onSyncDataReceived: () => {},
  onSyncDataRequest: () => {},
  onSyncDisconnected: () => {},
  removeSyncListeners: () => {},

  // BrowserView 相关（Web 环境不可用，设为 undefined 以便检测）
  bvEnable: undefined,
  bvDisable: undefined,
  bvSet: undefined,
  bvSend: () => Promise.resolve(),
  bvSendWithImages: () => Promise.resolve(),
  bvEval: () => Promise.resolve(),
  bvRefresh: () => Promise.resolve(),
  bvClickDeepThinking: () => Promise.resolve(),
  bvClickNetwork: () => Promise.resolve(),
  bvCaptureScreenshot: () => Promise.resolve(null),
  bvGetContent: () => Promise.resolve(''),
  bvGeneratePdf: () => Promise.resolve(),
  getAISites: () => Promise.resolve([]),

  // 知识库预览（Web 环境使用 iframe）
  kbPreviewCreate: () => Promise.resolve(),
  kbPreviewUpdateBounds: () => Promise.resolve(),
  kbPreviewDestroy: () => Promise.resolve(),
  fetchPageTitle: async (url) => {
    try {
      const response = await request('/proxy', {
        method: 'POST',
        body: { url, method: 'GET' }
      });
      const match = response.data?.match(/<title>([^<]*)<\/title>/i);
      return match ? match[1] : '';
    } catch {
      return '';
    }
  },

  // 局域网同步（Web 环境使用 WebSocket）
  getLocalIP: () => Promise.resolve(''),
  startSyncServer: () => Promise.resolve({ success: false, error: 'Web 环境不支持' }),
  stopSyncServer: () => Promise.resolve(),
  connectToSyncServer: () => Promise.resolve({ success: false, error: 'Web 环境不支持' }),
  disconnectFromSyncServer: () => Promise.resolve(),
  broadcastSyncData: () => Promise.resolve(),
  sendSyncData: () => Promise.resolve(),
  sendDataToHost: () => Promise.resolve(),
  requestSyncData: () => Promise.resolve(),

  // 数据导出/导入
  async exportAllUserData() {
    const config = await this.getConfig();
    const groups = await this.getGroups();
    const items = await this.getItems();
    
    const data = { config, groups, items, exportTime: new Date().toISOString() };
    const blob = new Blob([JSON.stringify(data, null, 2)], { type: 'application/json' });
    const url = URL.createObjectURL(blob);
    
    const a = document.createElement('a');
    a.href = url;
    a.download = `audit-portal-backup-${new Date().toISOString().slice(0, 10)}.json`;
    a.click();
    
    URL.revokeObjectURL(url);
    return { success: true };
  },

  async importAllUserData() {
    return new Promise((resolve) => {
      const input = document.createElement('input');
      input.type = 'file';
      input.accept = '.json';
      input.onchange = async (e) => {
        const file = e.target.files[0];
        if (file) {
          const reader = new FileReader();
          reader.onload = async (event) => {
            try {
              const data = JSON.parse(event.target.result);
              if (data.config) await this.saveConfig(data.config);
              if (data.groups) await this.saveGroups(data.groups);
              if (data.items) await this.saveItems(data.items);
              resolve({ success: true, data });
            } catch (err) {
              resolve({ success: false, error: err.message });
            }
          };
          reader.readAsText(file);
        } else {
          resolve({ success: false, error: '未选择文件' });
        }
      };
      input.click();
    });
  },

  // ========== 数据库管理 ==========
  async getDatabaseFiles() {
    return request('/database/files');
  },

  async getDatabaseFile(filename) {
    return request(`/database/files/${encodeURIComponent(filename)}`);
  },

  async updateDatabaseFile(filename, data) {
    return request(`/database/files/${encodeURIComponent(filename)}`, {
      method: 'PUT',
      body: { data }
    });
  },

  async deleteDatabaseFile(filename) {
    return request(`/database/files/${encodeURIComponent(filename)}`, {
      method: 'DELETE'
    });
  },

  async exportDatabase() {
    const token = typeof window !== 'undefined' && window.localStorage ? window.localStorage.getItem('token') : null;
    const headers = {};
    if (token) headers['Authorization'] = `Bearer ${token}`;
    const response = await fetch(`${API_BASE}/database/export`, { credentials: 'include', headers });
    if (!response.ok) {
      const err = await response.json().catch(() => ({ error: response.statusText }));
      throw new Error(err.error || '导出失败');
    }
    const blob = await response.blob();
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `database-export-${Date.now()}.json`;
    a.click();
    URL.revokeObjectURL(url);
    return { success: true };
  },

  async getDatabaseStats() {
    return request('/database/stats');
  }
};

// 创建统一的 API 对象
const createWebApi = () => {
  // Web 环境返回 Web API 实现
  console.log('[webApi] 使用 HTTP API');
  return webApiImpl;
};

// 立即挂载到 window（在模块加载时执行）
// 这样确保在其他模块访问 window.desktopAPI 之前已经挂载
if (typeof window !== 'undefined') {
  if (!window.desktopAPI) {
    window.desktopAPI = webApiImpl;
    console.log('[webApi] 已挂载 window.desktopAPI');
  }
}

// 导出 API
export const webApi = createWebApi();

export default webApi;
